import React, { useState, useCallback, useEffect } from "react";
import { Button, message, Form, Input, Row, Col, Popover } from 'antd';
import { updateWebColors } from "../../actions/operator-web-config.action";
import { EditOutlined } from "@ant-design/icons";

const { TextArea } = Input;

const DEFAULT_WEBSITE_COLORS = {
    primary_color: '#3b82f6',
    button_color: '#06C7E2',
    button_text_color: '#FFFFFF',
    bg_color: '#F4F2F0',
    discount_banner_color: '#023F82',
    discount_linear_bg: null,
    home_bg_color: '#FFFFFF',
    discount_banner_txt: '#FFFFFF',
    discount_banner_highlight: '#FFD700',
    footer_bg_color: '#01161A',
    contact_bg_color: '#064598',
    contact_linear_bg: null,
    menu_bg_color: '#FFFFFF',
    menu_txt_color: '#000000',
    banner_header: '#FFFFFF',
    banner_description: '#FFD700',
};

const COLOR_FIELDS = [
    { label: 'Primary color', name: 'primary_color', type: 'color' },
    { label: 'Button color', name: 'button_color', type: 'color' },
    { label: 'Button text color', name: 'button_text_color', type: 'color' },
    { label: 'Background color', name: 'bg_color', type: 'color' },
    { label: 'Discount banner color', name: 'discount_banner_color', type: 'color' },
    { label: 'Discount linear bg', name: 'discount_linear_bg', type: 'text', placeholder: 'e.g. linear-gradient(...) or leave empty' },
    { label: 'Home background color', name: 'home_bg_color', type: 'color' },
    { label: 'Discount banner text', name: 'discount_banner_txt', type: 'color' },
    { label: 'Discount banner highlight', name: 'discount_banner_highlight', type: 'color' },
    { label: 'Footer background color', name: 'footer_bg_color', type: 'color' },
    { label: 'Contact background color', name: 'contact_bg_color', type: 'color' },
    { label: 'Contact linear bg', name: 'contact_linear_bg', type: 'text', placeholder: 'e.g. linear-gradient(...) or leave empty' },
    { label: 'Menu background color', name: 'menu_bg_color', type: 'color' },
    { label: 'Menu text color', name: 'menu_txt_color', type: 'color' },
    { label: 'Banner header color', name: 'banner_header', type: 'color' },
    { label: 'Banner description color', name: 'banner_description', type: 'color' },
];

const normalizeForForm = (values) => {
    if (!values) return DEFAULT_WEBSITE_COLORS;
    const out = { ...DEFAULT_WEBSITE_COLORS, ...values };
    if (out.discount_linear_bg == null) out.discount_linear_bg = '';
    if (out.contact_linear_bg == null) out.contact_linear_bg = '';
    return out;
};

const isEmptyLinearValue = (v) =>
    v == null || v === '' || (typeof v === 'string' && !v.trim());

const normalizeForSave = (formValues) => {
    const out = { ...formValues };
    if (isEmptyLinearValue(out.discount_linear_bg)) out.discount_linear_bg = null;
    if (isEmptyLinearValue(out.contact_linear_bg)) out.contact_linear_bg = null;
    return out;
};

const LINEAR_KEYS = ['discount_linear_bg', 'contact_linear_bg'];

const linearValueEqual = (a, b) => {
    const emptyA = isEmptyLinearValue(a);
    const emptyB = isEmptyLinearValue(b);
    if (emptyA && emptyB) return true;
    if (emptyA || emptyB) return false;
    return String(a).trim() === String(b).trim();
};

const hasFormChanges = (currentValues, lastSaved) => {
    if (!lastSaved) return true;
    const current = normalizeForSave(currentValues);
    const keys = Object.keys(DEFAULT_WEBSITE_COLORS);
    for (const key of keys) {
        const c = current[key];
        const s = lastSaved[key];
        if (LINEAR_KEYS.includes(key)) {
            if (!linearValueEqual(c, s)) return true;
        } else {
            if (c !== s) return true;
        }
    }
    return false;
};

const getChangedColors = (lastSaved, currentNormalized) => {
    if (!lastSaved) return { ...currentNormalized };
    const keys = Object.keys(DEFAULT_WEBSITE_COLORS);
    const changed = {};
    for (const key of keys) {
        const c = currentNormalized[key];
        const s = lastSaved[key];
        const isEqual = LINEAR_KEYS.includes(key)
            ? linearValueEqual(c, s)
            : c === s;
        if (!isEqual) changed[key] = c;
    }
    return changed;
};

const HEX_REGEX = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const parseHex = (str) => {
    if (!str || typeof str !== 'string') return null;
    const s = str.trim();
    const m = s.match(HEX_REGEX);
    if (!m) return null;
    let hex = m[1];
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    return '#' + hex;
};

const isUsableAsBackground = (str) => {
    if (!str || typeof str !== 'string') return false;
    const s = str.trim();
    if (!s) return false;
    if (HEX_REGEX.test(s) || /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(s)) return true;
    if (/^linear-gradient\s*\(/i.test(s) || /^radial-gradient\s*\(/i.test(s)) return true;
    if (/^rgba?\s*\(/i.test(s)) return true;
    return false;
};

const GRADIENT_HEX_TOKEN_REGEX = /#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})/g;

const getFirstTwoHexTokens = (str) => {
    if (!str || typeof str !== 'string') return [];
    const tokens = [];
    for (const match of str.matchAll(GRADIENT_HEX_TOKEN_REGEX)) {
        const raw = match[0];
        const idx = match.index;
        if (typeof idx !== 'number') continue;
        const normalized = parseHex(raw);
        if (!normalized) continue;
        tokens.push({
            start: idx,
            end: idx + raw.length,
            normalized,
        });
        if (tokens.length >= 2) break;
    }
    return tokens;
};

const updateGradientColors = (currentStr, startHex, endHex) => {
    const s = currentStr != null ? String(currentStr).trim() : '';
    const normalizedStart = parseHex(startHex) || '#ff0000';
    const normalizedEnd = parseHex(endHex) || '#0000ff';
    const angleMatch = s.match(/^linear-gradient\s*\(\s*([^,]+?)\s*,/i);
    const angle = angleMatch?.[1]?.trim() || '90deg';
    return `linear-gradient(${angle}, ${normalizedStart}, ${normalizedEnd})`;
};

const GradientInputWithPreview = ({ value, onChange }) => {
    const str = value != null && value !== '' ? String(value).trim() : '';
    const showPreview = isUsableAsBackground(str);
    const previewStyle = showPreview ? { background: str } : {};

    const tokens = getFirstTwoHexTokens(str);
    const startColor = tokens[0]?.normalized || '#ff0000';
    const endColor = tokens[1]?.normalized || '#0000ff';

    const gradientEditor = (
        <div style={{ minWidth: 220 }}>
            <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: '1 1 120px' }}>
                    <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.8 }}>Start</div>
                    <Input
                        type="color"
                        value={startColor}
                        onChange={(e) => onChange(updateGradientColors(str, e.target.value, endColor))}
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ flex: '1 1 120px' }}>
                    <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.8 }}>End</div>
                    <Input
                        type="color"
                        value={endColor}
                        onChange={(e) => onChange(updateGradientColors(str, startColor, e.target.value))}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>
            <div style={{ fontSize: 12, marginTop: 8, opacity: 0.7 }}>
                Tip: you can also edit the full gradient text below.
            </div>
        </div>
    );

    return (
        <div className="gradient-input-with-preview">
            {showPreview ? (
                <Popover placement="bottom" trigger="click" content={gradientEditor}>
                    <div
                        className="gradient-preview-swatch"
                        style={previewStyle}
                        title="Click to edit gradient colors"
                    />
                </Popover>
            ) : (
                <Popover placement="bottom" trigger="click" content={gradientEditor}>
                    <Button size="small"><EditOutlined /></Button>
                </Popover>
            )}
            <TextArea
                className="gradient-text-input"
                placeholder="e.g. linear-gradient(90deg, #ff0, #f00) or leave empty"
                value={str}
                onChange={(e) => onChange(e.target.value)}
                allowClear
                rows={2}
                autoSize={{ minRows: 2, maxRows: 6 }}
            />
        </div>
    );
};

const ColorPickerWithHex = ({ value = '#000000', onChange }) => {
    const hexValue = value && typeof value === 'string' ? value : '#000000';
    const validHexForPicker = parseHex(hexValue) || '#000000';
    const handleColorChange = (e) => {
        const v = e.target.value;
        if (v) onChange(v);
    };
    const handleHexChange = (e) => {
        onChange(e.target.value || '');
    };
    const handleHexBlur = (e) => {
        const parsed = parseHex(e.target.value);
        if (parsed) onChange(parsed);
    };
    return (
        <div className="color-picker-with-hex">
            <Input
                type="color"
                className="color-input"
                value={validHexForPicker}
                onChange={handleColorChange}
            />
            <Input
                className="color-hex-input"
                placeholder="#FFFFFF"
                value={hexValue}
                onChange={handleHexChange}
                onBlur={handleHexBlur}
                maxLength={9}
            />
        </div>
    );
};

export const OperatorWebsiteColorsTab = ({ initialValues, onValuesChange, active }) => {
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [values, setValues] = useState(normalizeForForm(initialValues));
    const [lastSaved, setLastSaved] = useState(() =>
        normalizeForSave(normalizeForForm(initialValues))
    );

    useEffect(() => {
        if (active) form.setFieldsValue(normalizeForForm(values));
    }, [active, values, form]);

    const hasChanges = hasFormChanges(values, lastSaved);

    const handleSave = useCallback(() => {
        form.validateFields().then((formValues) => {
            setSaving(true);
            const merged = { ...values, ...formValues };
            const toStore = normalizeForSave(merged);
            const changed = getChangedColors(lastSaved, toStore);
            const payload = { ...changed };
            setValues(normalizeForForm(toStore));
            setLastSaved({ ...toStore, ...(lastSaved?.id != null && { id: lastSaved.id }) });
            if (onValuesChange) onValuesChange(toStore);
            if (Object.keys(changed).length === 0) {
                setSaving(false);
                return;
            }
            updateWebColors(payload, () => {
                setSaving(false);
                message.success('Colors saved successfully.');
            }, (err) => {
                message.error(err || 'Failed to save colors');
                setSaving(false);
            });
        }).catch(() => {});
    }, [form, onValuesChange, values, lastSaved]);

    const handleValuesChange = useCallback((_, formValues) => {
        const next = { ...values, ...formValues };
        setValues(next);
        if (onValuesChange) onValuesChange(normalizeForSave(next));
    }, [values, onValuesChange]);

    return (
        <div className="tab-content tab-content-colors">
            <p className="tab-description">
                Set theme colors for your customer website. Use text inputs for gradient values (e.g. linear-gradient).
            </p>
            <Form
                form={form}
                layout="vertical"
                initialValues={normalizeForForm(values)}
                onValuesChange={handleValuesChange}
                className="config-form colors-form"
            >
                <Row gutter={[24, 16]}>
                    {COLOR_FIELDS.map(({ label, name, type, placeholder }) => (
                        <Col xs={24} sm={12} md={8} key={name}>
                            <Form.Item name={name} label={label}>
                                {type === 'text' ? (
                                    <GradientInputWithPreview />
                                ) : (
                                    <ColorPickerWithHex />
                                )}
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
                <div className="form-actions">
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleSave}
                        loading={saving}
                        disabled={!hasChanges}
                    >
                        Save Colors
                    </Button>
                </div>
            </Form>
        </div>
    );
};
